import React, { useState, useEffect } from 'react';
import { Manga } from '../types/manga';
import {
  OfflineChapterSummary,
  getDownloadedChapters,
  deleteChapter,
  deleteAllChaptersForManga,
  formatBytes,
} from '../services/mangaOfflineDB';
import { formatBackupDate } from '../utils/helpers';
import {
  X,
  BookOpen,
  Download,
  Trash2,
  HardDrive,
  Clock,
  Layers,
  Sparkles,
  ExternalLink,
} from 'lucide-react';
import { showConfirm } from './ConfirmDialog';
import { getReadingProgress } from '../services/readingProgress';

interface OfflineChapterListModalProps {
  isOpen: boolean;
  onClose: () => void;
  manga: Manga | null;
  onOpenReader: (chapterNum: number, availableChapterNums: number[]) => void;
  /** Omitted where no downloader is available (download library) */
  onOpenDownloader?: () => void;
  onChaptersChanged?: () => void;
}

export const OfflineChapterListModal: React.FC<OfflineChapterListModalProps> = ({
  isOpen,
  onClose,
  manga,
  onOpenReader,
  onOpenDownloader,
  onChaptersChanged,
}) => {
  const [chapters, setChapters] = useState<OfflineChapterSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const loadChapters = () => {
    if (!manga) return;
    setIsLoading(true);
    getDownloadedChapters(manga.id)
      .then((list) => {
        setChapters(list);
        setIsLoading(false);
      })
      .catch(() => setIsLoading(false));
  };

  useEffect(() => {
    if (isOpen && manga) {
      loadChapters();
    }
  }, [isOpen, manga?.id]);

  if (!isOpen || !manga) return null;

  const totalBytes = chapters.reduce((sum, c) => sum + (c.sizeBytes || 0), 0);
  const totalPages = chapters.reduce((sum, c) => sum + (c.imageCount || 0), 0);

  const handleDeleteOne = async (chapterNum: number) => {
    if (
      await showConfirm({
        title: `ลบตอนที่ ${chapterNum}?`,
        message: 'ไฟล์ของตอนนี้จะถูกลบออกจากเครื่องเพื่อคืนพื้นที่ ดาวน์โหลดใหม่ได้ภายหลัง',
        confirmLabel: 'ลบตอนนี้',
        danger: true,
      })
    ) {
      await deleteChapter(manga.id, chapterNum);
      loadChapters();
      onChaptersChanged?.();
    }
  };

  const handleDeleteAll = async () => {
    if (
      await showConfirm({
        title: `ลบทั้งหมด ${chapters.length} ตอน?`,
        message: `ตอนที่ดาวน์โหลดไว้ของ "${manga.title}" จะถูกลบออกจากเครื่องทั้งหมด`,
        confirmLabel: 'ลบทั้งหมด',
        danger: true,
      })
    ) {
      await deleteAllChaptersForManga(manga.id);
      loadChapters();
      onChaptersChanged?.();
    }
  };

  // Chapters already read: up to the card's "read" count, or a chapter the reader saw to the end
  const progress = getReadingProgress(manga.id);
  const readUpTo = Math.max(
    Number(manga.readCh) || 0,
    progress ? (progress.finished ? progress.chapterNum : progress.chapterNum - 1) : 0
  );
  const readChapters = chapters.filter((c) => c.chapterNum <= readUpTo);

  const handleDeleteRead = async () => {
    if (readChapters.length === 0) return;
    if (
      await showConfirm({
        title: `ลบตอนที่อ่านแล้ว ${readChapters.length} ตอน?`,
        message: `ตอน ${readChapters.map((c) => c.chapterNum).join(', ')} จะถูกลบออกจากเครื่อง ตอนที่ยังไม่อ่านจะยังอยู่`,
        confirmLabel: 'ลบตอนที่อ่านแล้ว',
        danger: true,
      })
    ) {
      for (const c of readChapters) await deleteChapter(manga.id, c.chapterNum);
      loadChapters();
      onChaptersChanged?.();
    }
  };

  const handleRead = (chapterNum: number) => {
    const allNums = chapters.map((c) => c.chapterNum);
    onOpenReader(chapterNum, allNums);
  };

  return (
    <div className="modal-overlay safe-area-modal">
      <div onClick={(e) => e.stopPropagation()} className="modal-panel max-w-lg">
        {/* Header */}
        <div className="modal-header">
          <div className="min-w-0">
            <h3 className="modal-title">คลังตอนออฟไลน์</h3>
            <p className="text-xs text-zinc-500 truncate mt-0.5">{manga.title}</p>
          </div>
          <button onClick={onClose} title="ปิด" className="icon-btn -mr-2">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Storage Summary Bar */}
        <div className="px-5 py-3 border-b border-white/[0.06] flex flex-wrap items-center justify-between gap-x-3 gap-y-2 shrink-0">
          <div className="flex items-center gap-2 text-xs text-zinc-400 min-w-0">
            <HardDrive className="w-4 h-4 text-zinc-500 shrink-0" />
            <span className="truncate">
              <span className="text-zinc-200 font-medium">{formatBytes(totalBytes)}</span> · {chapters.length} ตอน · {totalPages} ภาพ
            </span>
          </div>

          <div className="flex items-center gap-1 shrink-0">
            {readChapters.length > 0 && readChapters.length < chapters.length && (
              <button type="button" onClick={handleDeleteRead} className="btn btn-sm btn-danger px-2.5">
                ลบที่อ่านแล้ว ({readChapters.length})
              </button>
            )}
            {chapters.length > 0 && (
              <button type="button" onClick={handleDeleteAll} className="btn btn-sm btn-danger px-2.5">
                ลบทั้งหมด
              </button>
            )}
            {onOpenDownloader && (
              <button
                type="button"
                onClick={() => {
                  onClose();
                  onOpenDownloader();
                }}
                className="btn btn-sm btn-secondary"
              >
                <Download className="w-3.5 h-3.5" />
                <span>ดาวน์โหลดเพิ่ม</span>
              </button>
            )}
          </div>
        </div>

        {/* Chapter List */}
        <div className="modal-body" style={{ WebkitOverflowScrolling: 'touch' }}>
          {isLoading && (
            <div className="py-12 text-center text-xs text-zinc-500">กำลังโหลดรายการตอนที่ดาวน์โหลด...</div>
          )}

          {!isLoading && chapters.length === 0 && (
            <div className="py-12 flex flex-col items-center justify-center text-center gap-4">
              <div className="w-12 h-12 rounded-full bg-white/[0.04] ring-1 ring-inset ring-white/[0.08] flex items-center justify-center text-zinc-400">
                <Download className="w-5 h-5" />
              </div>
              <div>
                <p className="text-sm text-zinc-200">ยังไม่มีตอนที่ดาวน์โหลดไว้ในเครื่อง</p>
                <p className="text-xs text-zinc-500 mt-1">เลือกตอนที่ต้องการโหลดเพื่อพกไปอ่านเวลาเน็ตหลุดได้เลย</p>
              </div>
              {onOpenDownloader && (
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    onOpenDownloader();
                  }}
                  className="btn btn-primary"
                >
                  <Download className="w-4 h-4" />
                  <span>ดาวน์โหลดตอน</span>
                </button>
              )}
            </div>
          )}

          {!isLoading && chapters.length > 0 && (
            <>
              <p className="text-xs text-zinc-500 mb-3">แตะที่ตอนเพื่อเปิดอ่านแบบ Webtoon เต็มจอ ไม่มีโฆษณา</p>
              <div className="rounded-xl ring-1 ring-inset ring-white/[0.06] divide-y divide-white/[0.06] overflow-hidden">
                {chapters.map((ch) => (
                  <div
                    key={ch.id}
                    className="px-3 py-2.5 flex items-center justify-between gap-3 hover:bg-white/[0.03] transition-colors"
                  >
                    <div onClick={() => handleRead(ch.chapterNum)} className="flex-1 min-w-0 cursor-pointer">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-zinc-100 tabular-nums">ตอนที่ {ch.chapterNum}</span>
                        {ch.sourceName && (
                          <span className="text-[10px] px-1.5 py-[3px] rounded bg-white/[0.06] text-zinc-400 leading-none">
                            {ch.sourceName}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 text-[11px] text-zinc-500 mt-1">
                        <span>{ch.imageCount} ภาพ</span>
                        <span>·</span>
                        <span>{formatBytes(ch.sizeBytes)}</span>
                        <span>·</span>
                        <span>{formatBackupDate(ch.downloadedAt)}</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-1 shrink-0">
                      <button type="button" onClick={() => handleRead(ch.chapterNum)} className="btn btn-sm btn-primary">
                        <BookOpen className="w-3.5 h-3.5" />
                        <span>อ่าน</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteOne(ch.chapterNum)}
                        title="ลบตอนนี้"
                        className="icon-btn w-8 h-8 hover:text-rose-400 hover:bg-rose-500/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
